import { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
// @mui
import {
  Card,
  Stack,
  Container,
  Typography,
  Button,
  Divider,
  Chip,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
// components
import Label from '../components/label';
import Iconify from '../components/iconify';
import { formatUtcDateTimeAsSaoPaulo } from '../utils/formatTime';
// context
import { EntityContext } from '../entity';
import { AccountContext } from '../account';

// ----------------------------------------------------------------------

export default function MySignatureDetailsPage() {
  const { envelopeKey } = useParams();
  const navigate = useNavigate();
  
  const [envelopeDetails, setEnvelopeDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [jwtToken, setJwtToken] = useState(null);

  const { getMyEnvelopeDetails } = useContext(EntityContext);
  const { getSession } = useContext(AccountContext);
  const getMyEnvelopeDetailsRef = useRef(getMyEnvelopeDetails);
  const getSessionRef = useRef(getSession);

  // Atualizar refs quando as funções mudarem
  useEffect(() => {
    getMyEnvelopeDetailsRef.current = getMyEnvelopeDetails;
  }, [getMyEnvelopeDetails]);

  useEffect(() => {
    getSessionRef.current = getSession;
  }, [getSession]);

  // Obter token JWT
  useEffect(() => {
    getSessionRef.current()
      .then((session) => {
        setJwtToken(session.idToken.jwtToken);
      })
      .catch((error) => {
        console.error('Erro ao obter sessão:', error);
      });
  }, []);

  // Carregar detalhes do envelope
  useEffect(() => {
    const loadEnvelopeDetails = async () => {
      if (!jwtToken || !envelopeKey) return;
      
      try {
        setIsLoading(true);
        const data = await getMyEnvelopeDetailsRef.current(jwtToken, envelopeKey);
        setEnvelopeDetails(data);
      } catch (error) {
        console.error('Erro ao carregar detalhes do envelope:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadEnvelopeDetails();
  }, [jwtToken, envelopeKey]);

  const getStatusLabel = (status) => {
    const statusMap = {
      'running': { text: 'Em Andamento', color: 'warning' },
      'finished': { text: 'Assinado', color: 'success' },
      'cancelled': { text: 'Cancelado', color: 'error' },
    };
    return statusMap[status] || { text: status, color: 'default' };
  };

  const getSignatureStatusLabel = (status) => {
    const statusMap = {
      'pending': { text: 'Pendente', color: 'warning' },
      'signed': { text: 'Assinado', color: 'success' },
      'created': { text: 'Criado', color: 'info' },
    };
    return statusMap[status] || { text: status, color: 'default' };
  };

  const getRoleTypeLabel = (roleType) => {
    const roleMap = {
      'signer': 'Assinante',
      'observer': 'Observador',
      'part': 'Parte',
      'witness': 'Testemunha',
    };
    return roleMap[roleType] || roleType;
  };

  // Organizar signers em grupos e individuais
  const { groups, individualSigners } = useMemo(() => {
    if (!envelopeDetails?.signers) {
      return { groups: [], individualSigners: [] };
    }

    const groupsMap = new Map();
    const individualSigners = [];

    envelopeDetails.signers.forEach((signer) => {
      if (signer.envelope_group_id) {
        if (!groupsMap.has(signer.envelope_group_id)) {
          groupsMap.set(signer.envelope_group_id, {
            envelope_group_id: signer.envelope_group_id,
            signers: [],
          });
        }
        groupsMap.get(signer.envelope_group_id).signers.push(signer);
      } else {
        individualSigners.push(signer);
      }
    });

    return {
      groups: Array.from(groupsMap.values()),
      individualSigners,
    };
  }, [envelopeDetails]);

  // Função para download de arquivos
  const downloadFile = (fileUrl, fileName) => {
    if (!fileUrl) return;
    
    fetch(fileUrl, {
      method: 'GET',
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Erro ao fazer download');
        }
        return response.blob();
      })
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch((error) => {
        console.error('Erro ao fazer download:', error);
        alert('Erro ao fazer download. Tente novamente.');
      });
  };

  const handleDownloadDocument = (document) => {
    if (!document.url) return;
    const fileName = `${document.document_name || 'documento'}.pdf`;
    downloadFile(document.url, fileName);
  };

  const handleDownloadEnvelope = () => {
    const envelope = envelopeDetails.envelope;
    if (!envelope.url) return;
    
    const fileName = `${envelope.envelope_name || 'envelope'}.pdf`;
    downloadFile(envelope.url, fileName);
  };

  if (isLoading) {
    return (
      <Container>
        <Typography>Carregando...</Typography>
      </Container>
    );
  }

  if (!envelopeDetails) {
    return (
      <Container>
        <Typography>Envelope não encontrado</Typography>
        <Button onClick={() => navigate('/dashboard/my-signatures')}>
          Voltar
        </Button>
      </Container>
    );
  }

  const statusInfo = getStatusLabel(envelopeDetails.envelope?.status);

  return (
    <>
      <Helmet>
        <title> Detalhes da Assinatura </title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Detalhes da Assinatura
          </Typography>
          <Stack direction="row" spacing={2}>
            {envelopeDetails.envelope?.url && (
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadEnvelope}
              >
                Baixar Envelope
              </Button>
            )}
            <Button 
              variant="outlined" 
              startIcon={<Iconify icon="eva:arrow-back-fill" />}
              onClick={() => navigate('/dashboard/my-signatures')}
            >
              Voltar
            </Button>
          </Stack>
        </Stack>

        <Stack spacing={3}>
          {/* Informações do Envelope */}
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Informações do Envelope
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Nome:
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {envelopeDetails.envelope?.envelope_name}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Status:
                </Typography>
                <Label color={statusInfo.color}>{statusInfo.text}</Label>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Data de Criação:
                </Typography>
                <Typography variant="body1">
                  {envelopeDetails.envelope?.created_at
                    ? formatUtcDateTimeAsSaoPaulo(envelopeDetails.envelope.created_at) || '-'
                    : '-'}
                </Typography>
              </Stack>
            </Stack>
          </Card>

          {/* Documentos */}
          {envelopeDetails.documents && envelopeDetails.documents.length > 0 && (
            <Card sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Documentos
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Stack spacing={2}>
                {envelopeDetails.documents.map((document) => (
                  <Stack 
                    key={document.document_key} 
                    direction="row" 
                    alignItems="center" 
                    justifyContent="space-between"
                    sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                  >
                    <Typography variant="body1">
                      {document.document_name}
                    </Typography>
                    <IconButton
                      color="primary"
                      onClick={() => handleDownloadDocument(document)}
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            </Card>
          )}

          {/* Grupos */}
          {groups.length > 0 && (
            <Card sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Grupos
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Stack spacing={3}>
                {groups.map((group, groupIndex) => (
                  <Box key={group.envelope_group_id || groupIndex}>
                    <Typography variant="subtitle1" gutterBottom>
                      Grupo {groupIndex + 1}
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Nome</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Papel</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {group.signers.map((signer, index) => {
                            const signerStatus = getSignatureStatusLabel(signer.signature_status);
                            return (
                              <TableRow key={signer.envelope_signer_key || index}>
                                <TableCell>{signer.signer_name}</TableCell>
                                <TableCell>{signer.signer_email}</TableCell>
                                <TableCell>
                                  <Chip 
                                    label={getRoleTypeLabel(signer.role_type)} 
                                    size="small"
                                    color={signer.role_type === 'observer' ? 'info' : 'default'}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Label color={signerStatus.color}>{signerStatus.text}</Label>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ))}
              </Stack>
            </Card>
          )}

          {/* Assinantes Individuais */}
          {individualSigners.length > 0 && (
            <Card sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Assinantes Individuais
              </Typography>
              <Divider sx={{ my: 2 }} />
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Nome</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Papel</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {individualSigners.map((signer, index) => {
                      const signerStatus = getSignatureStatusLabel(signer.signature_status);
                      return (
                        <TableRow key={signer.envelope_signer_key || index}>
                          <TableCell>{signer.signer_name}</TableCell>
                          <TableCell>{signer.signer_email}</TableCell>
                          <TableCell>
                            <Chip 
                              label={getRoleTypeLabel(signer.role_type)} 
                              size="small"
                              color={signer.role_type === 'observer' ? 'info' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <Label color={signerStatus.color}>{signerStatus.text}</Label>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          )}

          {/* Resumo */}
          {envelopeDetails.summary && (
            <Card sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Resumo
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Total de Assinantes:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {envelopeDetails.summary.total_signers}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Total de Observadores:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {envelopeDetails.summary.total_observers}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Assinaturas Concluídas:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {envelopeDetails.summary.signed_count}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Assinaturas Pendentes:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {envelopeDetails.summary.pending_count}
                  </Typography>
                </Stack>
              </Stack>
            </Card>
          )}
        </Stack>
      </Container>
    </>
  );
}
